import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { AppLayout, GlassCard, LoadingSpinner, PageHeader } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Bell, Mail, MessageSquare, Smartphone, Settings, CheckCircle,
  AlertCircle, Clock, Calendar, DollarSign, GraduationCap, Target,
  Shield, Zap, Send, RefreshCw, X, ChevronRight, Info, Save,
  BellRing, BellOff, Volume2, VolumeX
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { motion } from 'framer-motion';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Notification category configurations
const NOTIFICATION_CATEGORIES = [
  {
    id: 'deadlines',
    label: 'Deadlines & Due Dates',
    description: 'Application deadlines, FAFSA dates, registration deadlines',
    icon: Calendar,
    color: 'red'
  },
  {
    id: 'financial',
    label: 'Financial Updates',
    description: 'Savings goals, funding gap changes, scholarship opportunities',
    icon: DollarSign,
    color: 'emerald'
  },
  {
    id: 'academic',
    label: 'Academic Progress',
    description: 'Test scores, grades, course completion, milestones',
    icon: GraduationCap,
    color: 'blue'
  },
  {
    id: 'goals',
    label: 'Goals & Milestones',
    description: 'Goal completions, new recommendations, progress updates',
    icon: Target,
    color: 'purple'
  },
  {
    id: 'security',
    label: 'Security Alerts',
    description: 'Login alerts, account changes, suspicious activity',
    icon: Shield,
    color: 'amber'
  },
  {
    id: 'ai_insights',
    label: 'AI Advisor Insights',
    description: 'New recommendations, personalized tips, action items',
    icon: Zap,
    color: 'pink'
  }
];

// Notification frequency options
const FREQUENCY_OPTIONS = [
  { value: 'instant', label: 'Instant', description: 'Get notified immediately' },
  { value: 'daily', label: 'Daily Digest', description: 'Summary once per day' },
  { value: 'weekly', label: 'Weekly Digest', description: 'Summary once per week' },
  { value: 'off', label: 'Off', description: 'No notifications' }
];

// Notification Preference Toggle
const NotificationToggle = ({ category, emailEnabled, smsEnabled, onToggleEmail, onToggleSms }) => {
  const Icon = category.icon;
  
  return (
    <div className="p-4 rounded-xl bg-slate-800/30 hover:bg-slate-800/50 transition-colors">
      <div className="flex items-start gap-4">
        <div className={`w-10 h-10 rounded-lg bg-${category.color}-500/20 flex items-center justify-center flex-shrink-0`}>
          <Icon className={`w-5 h-5 text-${category.color}-400`} />
        </div>
        
        <div className="flex-1">
          <h4 className="text-white font-medium">{category.label}</h4>
          <p className="text-slate-300 text-sm mt-0.5">{category.description}</p>
          
          <div className="flex items-center gap-6 mt-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <Switch 
                checked={emailEnabled} 
                onCheckedChange={onToggleEmail}
                className="data-[state=checked]:bg-blue-600"
              />
              <span className="text-sm text-slate-300 flex items-center gap-1">
                <Mail className="w-4 h-4" /> Email
              </span>
            </label>
            
            <label className="flex items-center gap-2 cursor-pointer">
              <Switch 
                checked={smsEnabled} 
                onCheckedChange={onToggleSms}
                className="data-[state=checked]:bg-emerald-600"
              />
              <span className="text-sm text-slate-300 flex items-center gap-1">
                <Smartphone className="w-4 h-4" /> SMS
              </span>
            </label>
          </div>
        </div>
      </div>
    </div>
  );
};

// Test Notification Card
const TestNotificationCard = ({ type, onTest, testing }) => {
  return (
    <div className="p-4 rounded-xl bg-slate-800/50 border border-slate-700">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {type === 'email' ? (
            <Mail className="w-5 h-5 text-blue-400" />
          ) : (
            <Smartphone className="w-5 h-5 text-emerald-400" />
          )}
          <div>
            <p className="text-white font-medium">Test {type === 'email' ? 'Email' : 'SMS'} Notification</p>
            <p className="text-slate-300 text-sm">Send a test message to verify your settings</p>
          </div>
        </div>
        <Button 
          onClick={() => onTest(type)}
          disabled={testing}
          variant="outline"
          size="sm"
          className="border-slate-600"
        >
          {testing ? (
            <RefreshCw className="w-4 h-4 animate-spin" />
          ) : (
            <>
              <Send className="w-4 h-4 mr-2" /> Send Test
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

// Main Notification Settings Component
export default function NotificationSettings() {
  const { user, getAuthHeader } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [activeTab, setActiveTab] = useState('preferences');
  
  // Notification preferences state
  const [preferences, setPreferences] = useState({
    email_enabled: true,
    sms_enabled: false,
    phone_number: '',
    email_frequency: 'instant',
    sms_frequency: 'instant',
    quiet_hours_enabled: false,
    quiet_hours_start: '22:00',
    quiet_hours_end: '08:00',
    categories: {
      deadlines: { email: true, sms: true },
      financial: { email: true, sms: false },
      academic: { email: true, sms: false },
      goals: { email: true, sms: false },
      security: { email: true, sms: true },
      ai_insights: { email: true, sms: false }
    }
  });
  
  // Recent notifications state
  const [recentNotifications, setRecentNotifications] = useState([]);

  useEffect(() => {
    fetchPreferences();
    fetchRecentNotifications();
  }, []);

  const fetchPreferences = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/notifications/preferences`, {
        ...getAuthHeader(),
        withCredentials: true
      });
      if (response.data) {
        setPreferences(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) {
      console.error('Failed to fetch preferences:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRecentNotifications = async () => {
    try {
      const response = await axios.get(`${API}/notifications/in-app`, {
        ...getAuthHeader(),
        withCredentials: true
      });
      setRecentNotifications(response.data.notifications || []);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  };

  const handleSavePreferences = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/notifications/preferences`, preferences, {
        ...getAuthHeader(),
        withCredentials: true
      });
      toast.success('Notification preferences saved!');
    } catch (error) {
      toast.error('Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  const handleTestNotification = async (type) => {
    setTesting(true);
    try {
      if (type === 'email') {
        await axios.post(`${API}/notifications/test-email`, {}, {
          ...getAuthHeader(),
          withCredentials: true
        });
        toast.success(`Test email sent to ${user?.email}`);
      } else {
        if (!preferences.phone_number) {
          toast.error('Please add a phone number first');
          setTesting(false);
          return;
        }
        await axios.post(`${API}/notifications/test-sms`, {
          phone_number: preferences.phone_number
        }, {
          ...getAuthHeader(),
          withCredentials: true
        });
        toast.success(`Test SMS sent to ${preferences.phone_number}`);
      }
    } catch (error) {
      const message = error.response?.data?.detail || `Failed to send test ${type}`;
      toast.error(message);
    } finally {
      setTesting(false);
    }
  };

  const updateCategoryPreference = (categoryId, channel, value) => {
    setPreferences(prev => ({
      ...prev,
      categories: {
        ...prev.categories,
        [categoryId]: {
          ...prev.categories[categoryId],
          [channel]: value
        }
      }
    }));
  };

  const toggleAllNotifications = (channel, enabled) => {
    const newCategories = { ...preferences.categories };
    Object.keys(newCategories).forEach(cat => {
      newCategories[cat][channel] = enabled;
    });
    setPreferences(prev => ({
      ...prev,
      categories: newCategories,
      [`${channel}_enabled`]: enabled
    }));
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner size="lg" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="space-y-6 max-w-4xl mx-auto">
        {/* Header */}
        <PageHeader 
          title="Notification Settings"
          description="Manage how and when you receive notifications"
          action={
            <Button 
              onClick={handleSavePreferences}
              disabled={saving}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {saving ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Save Changes
            </Button>
          }
        />
        
        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="w-full bg-slate-800/50 p-1 mb-6 grid grid-cols-3">
            <TabsTrigger value="preferences" className="data-[state=active]:bg-blue-600">
              <Settings className="w-4 h-4 mr-2" /> Preferences
            </TabsTrigger>
            <TabsTrigger value="channels" className="data-[state=active]:bg-blue-600">
              <Bell className="w-4 h-4 mr-2" /> Channels
            </TabsTrigger>
            <TabsTrigger value="history" className="data-[state=active]:bg-blue-600">
              <Clock className="w-4 h-4 mr-2" /> History
            </TabsTrigger>
          </TabsList>
          
          {/* Preferences Tab */}
          <TabsContent value="preferences">
            <div className="space-y-6">
              {/* Master Controls */}
              <GlassCard className="p-6">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <BellRing className="w-5 h-5 text-blue-400" />
                  Master Controls
                </h3>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="p-4 rounded-xl bg-slate-800/50 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Mail className="w-5 h-5 text-blue-400" />
                      <div>
                        <p className="text-white font-medium">Email Notifications</p>
                        <p className="text-slate-300 text-sm">{user?.email}</p>
                      </div>
                    </div>
                    <Switch 
                      checked={preferences.email_enabled}
                      onCheckedChange={(v) => toggleAllNotifications('email', v)}
                      className="data-[state=checked]:bg-blue-600"
                    />
                  </div>
                  
                  <div className="p-4 rounded-xl bg-slate-800/50 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Smartphone className="w-5 h-5 text-emerald-400" />
                      <div>
                        <p className="text-white font-medium">SMS Notifications</p>
                        <p className="text-slate-300 text-sm">{preferences.phone_number || 'Not configured'}</p>
                      </div>
                    </div>
                    <Switch 
                      checked={preferences.sms_enabled}
                      onCheckedChange={(v) => toggleAllNotifications('sms', v)}
                      className="data-[state=checked]:bg-emerald-600"
                    />
                  </div>
                </div>
              </GlassCard>
              
              {/* Category Preferences */}
              <GlassCard className="p-6">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Bell className="w-5 h-5 text-purple-400" />
                  Notification Categories
                </h3>
                
                <div className="space-y-3">
                  {NOTIFICATION_CATEGORIES.map(category => (
                    <NotificationToggle
                      key={category.id}
                      category={category}
                      emailEnabled={preferences.categories[category.id]?.email || false}
                      smsEnabled={preferences.categories[category.id]?.sms || false}
                      onToggleEmail={(v) => updateCategoryPreference(category.id, 'email', v)}
                      onToggleSms={(v) => updateCategoryPreference(category.id, 'sms', v)}
                    />
                  ))}
                </div>
              </GlassCard>
              
              {/* Quiet Hours */}
              <GlassCard className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <VolumeX className="w-5 h-5 text-amber-400" />
                    <h3 className="text-lg font-semibold text-white">Quiet Hours</h3>
                  </div>
                  <Switch 
                    checked={preferences.quiet_hours_enabled}
                    onCheckedChange={(v) => setPreferences(prev => ({ ...prev, quiet_hours_enabled: v }))}
                    className="data-[state=checked]:bg-amber-600"
                  />
                </div>
                
                <p className="text-slate-300 text-sm mb-4">
                  Pause non-urgent notifications during these hours
                </p>
                
                {preferences.quiet_hours_enabled && (
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-300">Start Time</Label>
                      <Input
                        type="time"
                        value={preferences.quiet_hours_start}
                        onChange={(e) => setPreferences(prev => ({ ...prev, quiet_hours_start: e.target.value }))}
                        className="mt-1 bg-slate-800 border-slate-700"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-300">End Time</Label>
                      <Input
                        type="time"
                        value={preferences.quiet_hours_end}
                        onChange={(e) => setPreferences(prev => ({ ...prev, quiet_hours_end: e.target.value }))}
                        className="mt-1 bg-slate-800 border-slate-700"
                      />
                    </div>
                  </div>
                )}
              </GlassCard>
            </div>
          </TabsContent>
          
          {/* Channels Tab */}
          <TabsContent value="channels">
            <div className="space-y-6">
              {/* Email Settings */}
              <GlassCard className="p-6">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Mail className="w-5 h-5 text-blue-400" />
                  Email Settings
                </h3>
                
                <div className="space-y-4">
                  <div>
                    <Label className="text-slate-300">Email Address</Label>
                    <Input
                      type="email"
                      value={user?.email || ''}
                      disabled
                      className="mt-1 bg-slate-800/50 border-slate-700 text-slate-300"
                    />
                    <p className="text-slate-300 text-xs mt-1">Email is tied to your account</p>
                  </div>
                  
                  <div>
                    <Label className="text-slate-300">Email Frequency</Label>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2">
                      {FREQUENCY_OPTIONS.map(option => (
                        <button
                          key={option.value}
                          onClick={() => setPreferences(prev => ({ ...prev, email_frequency: option.value }))}
                          className={`p-3 rounded-lg border text-left transition-colors ${
                            preferences.email_frequency === option.value
                              ? 'border-blue-500 bg-blue-500/10 text-white'
                              : 'border-slate-700 hover:border-slate-600 text-slate-300'
                          }`}
                        >
                          <p className="font-medium text-sm">{option.label}</p>
                          <p className="text-xs text-slate-300">{option.description}</p>
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  <TestNotificationCard 
                    type="email" 
                    onTest={handleTestNotification}
                    testing={testing}
                  />
                </div>
              </GlassCard>
              
              {/* SMS Settings */}
              <GlassCard className="p-6">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Smartphone className="w-5 h-5 text-emerald-400" />
                  SMS Settings
                </h3>
                
                <div className="space-y-4">
                  <div>
                    <Label className="text-slate-300">Phone Number</Label>
                    <Input
                      type="tel"
                      placeholder="+1 (555) 123-4567"
                      value={preferences.phone_number || ''}
                      onChange={(e) => setPreferences(prev => ({ ...prev, phone_number: e.target.value }))}
                      className="mt-1 bg-slate-800 border-slate-700"
                    />
                    <p className="text-slate-300 text-xs mt-1">Include country code for international numbers</p>
                  </div>
                  
                  <div>
                    <Label className="text-slate-300">SMS Frequency</Label>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2">
                      {FREQUENCY_OPTIONS.map(option => (
                        <button
                          key={option.value}
                          onClick={() => setPreferences(prev => ({ ...prev, sms_frequency: option.value }))}
                          className={`p-3 rounded-lg border text-left transition-colors ${
                            preferences.sms_frequency === option.value
                              ? 'border-emerald-500 bg-emerald-500/10 text-white'
                              : 'border-slate-700 hover:border-slate-600 text-slate-300'
                          }`}
                        >
                          <p className="font-medium text-sm">{option.label}</p>
                          <p className="text-xs text-slate-300">{option.description}</p>
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/20">
                    <div className="flex items-start gap-3">
                      <Info className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-amber-300 font-medium text-sm">SMS Charges May Apply</p>
                        <p className="text-slate-300 text-sm">
                          Standard messaging rates from your carrier may apply. We recommend email for non-urgent notifications.
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <TestNotificationCard 
                    type="sms" 
                    onTest={handleTestNotification}
                    testing={testing}
                  />
                </div>
              </GlassCard>
            </div>
          </TabsContent>
          
          {/* History Tab */}
          <TabsContent value="history">
            <GlassCard className="p-6">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Clock className="w-5 h-5 text-slate-300" />
                Recent Notifications
              </h3>
              
              {recentNotifications.length === 0 ? (
                <div className="text-center py-12">
                  <Bell className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-300">No notifications yet</p>
                  <p className="text-slate-300 text-sm">You'll see your notification history here</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {recentNotifications.map((notification, idx) => (
                    <div 
                      key={notification.id || idx}
                      className={`p-4 rounded-xl border transition-colors ${
                        notification.read 
                          ? 'bg-slate-800/30 border-slate-700' 
                          : 'bg-blue-500/10 border-blue-500/20'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                          <div className={`w-2 h-2 rounded-full mt-2 ${notification.read ? 'bg-slate-600' : 'bg-blue-400'}`} />
                          <div>
                            <p className="text-white font-medium">{notification.title}</p>
                            <p className="text-slate-300 text-sm mt-1">{notification.message}</p>
                            <p className="text-slate-300 text-xs mt-2">
                              {new Date(notification.created_at).toLocaleString()}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </GlassCard>
          </TabsContent>
        </Tabs>
      </div>
    </AppLayout>
  );
}
